// Single source of truth for facts about the business. Used by the SEO component (JSON-LD)
// and by scripts/generate-seo.mjs (sitemap, robots, llms.txt, rss). Keep it plain data, no imports.

export const SITE = {
  url: 'https://www.farwingstech.com',
  name: 'Farwings Tech Solutions',
  shortName: 'Farwings',
  tagline: 'Your Vision | Our Technology | Real Solutions',
  description:
    'Farwings Tech Solutions is an app development and web development company in India building custom web apps, mobile apps, SaaS platforms and AI automation for startups and businesses worldwide.',
  email: 'farwingsolutions@gmail.com',
  phones: ['+91-7043031553', '+91-8866823010'],
  logo: 'https://www.farwingstech.com/farwinglogo_transparent.png',
  locality: 'Ahmedabad',
  region: 'Gujarat',
  country: 'IN',
  language: 'en',
  // add real profile URLs here; they are published as schema.org sameAs and in llms.txt
  sameAs: ['https://www.instagram.com/farwingstech'],
};

export const ORG_ID = `${SITE.url}/#organization`;
export const WEBSITE_ID = `${SITE.url}/#website`;

export const absUrl = (path = '/') => `${SITE.url}${path === '/' ? '/' : path.replace(/\/+$/, '')}`;

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'ProfessionalService'],
    '@id': ORG_ID,
    name: SITE.name,
    alternateName: SITE.shortName,
    url: SITE.url,
    logo: { '@type': 'ImageObject', url: SITE.logo },
    image: SITE.logo,
    slogan: SITE.tagline,
    description: SITE.description,
    email: SITE.email,
    telephone: SITE.phones[0],
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      addressLocality: SITE.locality,
      addressRegion: SITE.region,
      addressCountry: SITE.country,
    },
    areaServed: 'Worldwide',
    contactPoint: SITE.phones.map((t) => ({
      '@type': 'ContactPoint',
      telephone: t,
      email: SITE.email,
      contactType: 'sales',
      availableLanguage: ['English', 'Hindi', 'Gujarati'],
    })),
    knowsAbout: [
      'Web development',
      'Mobile app development',
      'Custom software development',
      'SaaS development',
      'AI automation',
      'Search engine optimization',
      'Answer engine optimization',
      'Generative engine optimization',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'App & Web Development Services',
      itemListElement: [
        ['Custom Web Development', '/services/custom-web-development-company'],
        ['Mobile App Development', '/services/mobile-app-development-company'],
        ['SaaS Platform Development', '/services/saas-development-company'],
        ['AI Automation Services', '/services/ai-automation-services'],
        ['Custom Software Development', '/services/custom-software-development'],
      ].map(([name, path]) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name, url: `${SITE.url}${path}` },
      })),
    },
    ...(SITE.sameAs.length ? { sameAs: SITE.sameAs } : {}),
  };
}

export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: SITE.url,
    name: SITE.name,
    inLanguage: 'en',
    publisher: { '@id': ORG_ID },
  };
}

export function breadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: absUrl(it.path),
    })),
  };
}

export function faqSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

export function articleSchema(post) {
  const url = absUrl(`/blog/${post.slug}`);
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${url}#article`,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    headline: post.title,
    description: post.description,
    abstract: post.summary,
    datePublished: post.date,
    dateModified: post.updated || post.date,
    inLanguage: 'en',
    articleSection: post.category,
    keywords: post.tags.join(', '),
    image: SITE.logo,
    author: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
    isPartOf: { '@id': WEBSITE_ID },
  };
}

export function serviceSchema(slug, service) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${absUrl(`/services/${slug}`)}#service`,
    name: service.heroTitle,
    serviceType: service.title,
    description: service.metaDescription,
    url: absUrl(`/services/${slug}`),
    provider: { '@id': ORG_ID },
    areaServed: 'Worldwide',
  };
}
